/* Common conditional functions used with the conditional section container component */

import {
  __,
  compose,
  prop,
  propEq,
  anyPass,
  is,
  map,
  equals,
  any,
  defaultTo,
  both,
  complement,
  isNil,
  contains,
  gt as ramdaGt,
  gte as ramdaGte,
  lt as ramdaLt,
  lte as ramdaLte,
} from 'ramda';

/*
  Exported conditional functions

  The conditional section component calls the unary condition function and
  passes it the current form field values and the function should then return true or false
  depending on whether the condition is fulfilled.

  Here's an example function that would take the field name and compared value as arguments:
    const greaterThanOrEqual = (prop, value) => (formValues) => formValues[prop] > value

  Form values, that are passed to the function by the container component, are in key-value pairs. For example:
    { patientLength: 175, patientName: 'John Doe', ... }

  Ramda and arrow functions help us to keep the functions less verbose, see Ramda documentation for further
  reference. If you need custom, form specific, functionality; please keep those functions within the related
  forms. The functions exported here should be generic and easily reusable.

  Note: When dealing with functions that are not safe with null/undefined (like functions that require an array
  to iterate over), compose the function with defaultTo and give an empty fallback value as the parameter.
  See function 'has'
 */

const notEqual = complement(equals);
const notNil = complement(isNil);

// Greater than & greater than or equal
export const gt = (p, val) => compose(ramdaGt(__, val), prop(p));
export const gte = (p, val) => compose(ramdaGte(__, val), prop(p));

// Lower than & lower than or equal
export const lt = (p, val) => compose(ramdaLt(__, val), prop(p));
export const lte = (p, val) => compose(ramdaLte(__, val), prop(p));

// is, i.e. field = value
export const fieldIs = propEq;

// Opposite of above
export const fieldIsnt = complement(propEq);

export const fieldExists = field => compose(notNil, prop(field));

// Checks if the field is a given type
export const fieldIsType = (field, type) => compose(is(type), prop(field));

// field exists and is not a value
export const neq = (p, val) => compose(both(notNil, notEqual(val)), prop(p));

// Checks whether the value is one of the values in a given list.
export const oneOf = (p, values) => compose(anyPass(map(v => equals(v), values)), prop(p));

// Check if the value is in an array of values
export const hasOne = (p, values) => compose(anyPass(map(v => contains(v), values)), defaultTo([]), prop(p));

// Checks whether the value (Array) has a value
export const has = (p, val) => compose(any(equals(val)), defaultTo([]), prop(p));
